<template>
  <div class="csp">
    <template v-if="loading">
      <Skeleton
        v-for="index in 2"
        :key="index"
        height="2.5rem"
        class="csp__skeleton"
      />
    </template>

    <div
      v-else-if="loadError"
      class="csp__empty csp__empty--error"
    >
      <i class="fa-light fa-triangle-exclamation" />
      <span>{{ t('dataExplorer.contactFile.fields.sourceProfiles.error') }}</span>
    </div>

    <div
      v-else-if="!profiles.length"
      class="csp__empty"
    >
      <i class="fa-light fa-store" />
      <span>{{ t('dataExplorer.contactFile.fields.sourceProfiles.empty') }}</span>
    </div>

    <ul
      v-else
      class="csp__list"
    >
      <li
        v-for="profile in profiles"
        :key="profile.idShopCustomer"
        class="csp__row"
        data-test="contact-source-profile"
      >
        <div class="csp__icon">
          <i :class="getProfileIcon(profile)" />
        </div>
        <div class="csp__content">
          <div class="csp__line">
            <span
              v-if="profile.sourceResolution === 'import'"
              v-tooltip.top="t('dataExplorer.contactFile.fields.sourceProfiles.importHint')"
              class="csp__import"
              data-test="contact-source-profile-import"
            >{{ t('dataExplorer.contactFile.fields.sourceProfiles.import') }}</span>
            <span
              v-else-if="profile.source"
              class="csp__source"
              :title="getContactProfileSourceLabel(profile)"
              data-test="contact-source-profile-source"
            >{{ getContactProfileSourceLabel(profile) }}</span>
            <span
              v-if="profile.source && profile.sourceResolution === 'default'"
              v-tooltip.top="t('dataExplorer.contactFile.fields.sourceProfiles.inferredHint')"
              class="csp__inferred"
              data-test="contact-source-profile-inferred"
            >{{ t('dataExplorer.contactFile.fields.sourceProfiles.defaultSource') }}</span>
            <span
              v-tooltip.top="t(`dataExplorer.contactFile.fields.sourceProfiles.stateHint.${getContactProfileState(profile)}`)"
              class="csp__state"
              :class="`csp__state--${getContactProfileState(profile)}`"
              data-test="contact-source-profile-state"
            >{{ t(`dataExplorer.contactFile.fields.sourceProfiles.state.${getContactProfileState(profile)}`) }}</span>
          </div>
          <div class="csp__line csp__line--meta">
            <span
              class="csp__id"
              :title="t('dataExplorer.contactFile.fields.sourceProfiles.identifier')"
            >{{ profile.idCustomerShop || '-' }}</span>
            <span
              v-if="profile.dateCreation"
              class="csp__date"
            >{{ t('dataExplorer.contactFile.fields.sourceProfiles.createdOn', { date: formatDate(profile.dateCreation, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY') }) }}</span>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Skeleton from 'primevue/skeleton';
import Tooltip from 'primevue/tooltip';

import {
  getContactProfileSourceLabel,
  getContactProfileState,
  getContactSourceProfilesNest,
} from '@/composables/data-explorer/contactSourceProfiles';
import { formatDate } from '@/helpers/Date';
import { ContactSourceProfile } from '@/types/data-explorer-types';

/**
 * Fiches client d'un contact, une par point de vente, avec leur identifiant
 * dans ce point de vente. Lecture seule : ni fusion ni action.
 */
export default defineComponent({
  name: 'ContactSourceProfiles',

  components: {
    Skeleton,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    idShopContact: {
      type: Number,
      required: true,
    },
  },

  emits: ['loaded'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const profiles = ref<ContactSourceProfile[]>([]);
    const loading = ref(true);
    const loadError = ref(false);

    // La fiche contact est réutilisée d'un onglet à l'autre : seule la réponse
    // du dernier contact demandé est gardée.
    let lastRequest = 0;

    const load = async (idShopContact: number) => {
      lastRequest += 1;
      const request = lastRequest;
      loading.value = true;
      loadError.value = false;

      const result = idShopContact ? await getContactSourceProfilesNest(idShopContact) : [];
      if (request !== lastRequest) {
        return;
      }

      profiles.value = result ?? [];
      loadError.value = result === null;
      loading.value = false;
      emit('loaded', profiles.value.length);
    };

    watch(() => props.idShopContact, (idShopContact) => {
      load(idShopContact);
    }, { immediate: true });

    const getProfileIcon = (profile: ContactSourceProfile): string => {
      if (profile.sourceResolution === 'import') {
        return 'fa-light fa-file-import';
      }

      return profile.source && profile.source.parent ? 'fa-light fa-store' : 'fa-light fa-database';
    };

    return {
      t,
      profiles,
      loading,
      loadError,
      formatDate,
      getContactProfileSourceLabel,
      getContactProfileState,
      getProfileIcon,
    };
  },
});
</script>

<style lang="scss" scoped>
// Mêmes repères visuels que les lignes de la fiche contact (cf-info-row).
.csp {
  &__skeleton {
    margin: 0.375rem 1rem;
    width: auto !important;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__row {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.5rem 1rem;
    transition: background 0.1s;

    &:hover {
      background: #f9fafb;
    }
  }

  &__icon {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 0.8125rem;
      color: #64748b;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.1875rem;
  }

  &__line {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    min-width: 0;

    &--meta {
      flex-wrap: wrap;
    }
  }

  &__source {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  &__import {
    display: inline-flex;
    align-items: center;
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 0.125rem 0.5rem;
    border-radius: 20px;
    background: #e0e7ff;
    color: #3730a3;
    white-space: nowrap;
    cursor: help;
  }

  &__inferred {
    font-size: 0.6875rem;
    color: #94a3b8;
    font-style: italic;
    white-space: nowrap;
    cursor: help;
  }

  &__state {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 0.125rem 0.5rem;
    border-radius: 20px;
    white-space: nowrap;
    flex-shrink: 0;
    cursor: help;

    &--active {
      background: #dcfce7;
      color: #166534;
    }

    &--inactive {
      background: #f1f5f9;
      color: #475569;
    }

    &--deleted {
      background: #fee2e2;
      color: #991b1b;
    }
  }

  &__id {
    font-size: 0.75rem;
    color: #64748b;
    font-weight: 600;
    background: #f1f5f9;
    padding: 0.0625rem 0.4375rem;
    border-radius: 4px;
    font-family: monospace;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__date {
    font-size: 0.75rem;
    color: #6b7280;
    white-space: nowrap;
  }

  &__empty {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 1rem 0.5rem;
    color: #9ca3af;
    font-size: 0.8125rem;
    font-style: italic;

    i {
      font-size: 0.875rem;
    }

    &--error {
      color: #b45309;
    }
  }
}
</style>
